import React from 'react';

import { Icon } from '@rbilabs/universal-components';
import { logButtonClick } from 'features/navigation/utils/analytics';

import { ProductDetailListItem } from 'components/product-detail-list-item';
import { ProductDetailListItemGroup } from 'components/product-detail-list-item-group';
import { transformUserSelectionsToSummaryItems } from 'components/product-detail-v2/order-summary/utils';
import { useProductPropsSelections } from 'components/product-detail-v2/use-product-props-selections';
import { FallbackImageSvg } from 'components/svgs/fallback-image';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ClickEventComponentNames } from 'state/amplitude';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { ProductWizardStep } from 'state/product-wizard-v2/types';

import { ProductComboFlowContainer, ProductComboFlowInnerLayout } from './styled';

export const ProductComboFlow: React.FC = () => {
  const { formatCalories } = useMenuCalories();
  const { selectedProduct, userSelections } = useProductWizardContext();

  const { goToCustomizeProduct } = useProductWizardFlow();
  const { priceForComboSlotSelection } = useMenuContext();
  const { buildProductPropsSelections } = useProductPropsSelections();

  const mappedItems = React.useMemo(() => {
    if (!selectedProduct) {
      return [];
    }

    return transformUserSelectionsToSummaryItems({
      buildProductPropsSelections,
      priceForComboSlotSelection,
      selectedProduct,
      userSelections,
    });
  }, [buildProductPropsSelections, priceForComboSlotSelection, selectedProduct, userSelections]);

  const showImages = mappedItems.some(item => !!item.productListItemProps.productProps.image);

  const handleProductCustomization = React.useCallback(
    (slotKey: string) => {
      goToCustomizeProduct({ slotKey, step: ProductWizardStep.ProductCustomization });
    },
    [goToCustomizeProduct]
  );

  const logEventProductCustomization = (name: string) => {
    logButtonClick({
      attributes: {
        component: ClickEventComponentNames.PRODUCT_HOME_EDIT_ITEM,
        name,
      },
    });
  };

  if (!selectedProduct) {
    return null;
  }

  return (
    <ProductComboFlowContainer testID="product-combo-flow">
      <ProductComboFlowInnerLayout>
        <ProductDetailListItemGroup
          items={mappedItems.map(item => (
            <ProductDetailListItem
              key={item.slotKey}
              {...item.productListItemProps}
              productProps={{
                ...item.productListItemProps.productProps,
                fallbackImage: showImages
                  ? props => <FallbackImageSvg {...(props || {})} />
                  : undefined,
                calories: formatCalories(item.productListItemProps.productProps.calories),
              }}
              controls={
                item.productListItemProps.isClickable && <Icon variant="next" testID="next-icon" />
              }
              onNavigate={() => {
                handleProductCustomization(item.slotKey);
                logEventProductCustomization(item.productListItemProps.productProps.name ?? '');
              }}
            />
          ))}
        />
      </ProductComboFlowInnerLayout>
    </ProductComboFlowContainer>
  );
};
